import CircleIcon from "../ToolMenu/icons/circle";
import Rectangle from "../ToolMenu/icons/rect";
import Polygon from "../ToolMenu/icons/polygon";
import styles from "./ShapeEditor.scss";
import { Check, X } from "lucide-react";
import React from "react";
import { Aabox, Box, Circle, Contour, Entity } from "app/js/types";
import classNames from "classnames";

type ShapeChoice = "circle" | "aabox" | "contour";

type ShapeProperties =
  | {
      aabox: Aabox;
      circle: null;
      contour: null;
      box: null;
    }
  | {
      aabox: null;
      circle: Circle;
      contour: null;
      box: null;
    }
  | {
      aabox: null;
      circle: null;
      contour: Contour;
      box: null;
    };
export type OnApplyCallback = (
  entityId: number,
  newShape: ShapeProperties,
) => void;

interface ShapeEditorProps {
  entity: Entity;
  onApply: OnApplyCallback;
  onClose: () => void;
}

function stringifyShape(shape: Aabox | Circle | Box) {
  return JSON.stringify(shape, null, 2);
}

function stringifyContour(shape: Contour) {
  const halfStringified = shape.map((pair) => JSON.stringify(pair));
  return `[\n  ${halfStringified.join(",\n  ")}\n]`;
}

export default function ShapeEditor({
  entity,
  onApply,
  onClose,
}: ShapeEditorProps) {
  let defaultAabox = stringifyShape({ x: 0, y: 0, width: 0, height: 0 });
  let defaultCircle = stringifyShape({ x: 0, y: 0, radius: 0 });
  let defaultContour = stringifyContour([
    [0, 0],
    [0, 1],
    [1, 0],
  ]);
  // aabox is probably the most frequently used shape
  let defaultSelectedShape: ShapeChoice = "aabox";
  let defaultShape: string = defaultAabox;
  if (!!entity.aabox) {
    defaultAabox = stringifyShape(entity.aabox);
    defaultShape = defaultAabox;
  } else if (!!entity.circle) {
    defaultSelectedShape = "circle";
    defaultCircle = stringifyShape(entity.circle);
    defaultShape = defaultCircle;
  } else if (!!entity.contour) {
    defaultSelectedShape = "contour";
    defaultContour = stringifyContour(entity.contour);
    defaultShape = defaultContour;
  } else if (!!entity.box) {
    // Boxes are obsolete; force a user to use a contour instead
    defaultSelectedShape = "contour";
    // I might try and transform box -> contour automatically, but it is not worth the efforts: boxes are mostly from old test/demo teams
    // So, if one meets it, one has to convert it manually
    defaultContour = stringifyShape(entity.box);
    defaultShape = defaultContour;
  }
  const [selectedShape, setSelectedShape] =
    React.useState<ShapeChoice>(defaultSelectedShape);
  // I keep three separate states in case one would like to switch between them back and forth before deciding
  const [aaboxValue, setAaboxValue] = React.useState<string>(defaultAabox);
  const [circleValue, setCircleValue] = React.useState<string>(defaultCircle);
  const [contourValue, setContourValue] =
    React.useState<string>(defaultContour);
  const [shapeValue, setShapeValue] = React.useState<string>(defaultShape);

  const handleAaboxSelect = () => {
    setSelectedShape("aabox");
    setShapeValue(aaboxValue);
  };
  const handleCircleSelect = () => {
    setSelectedShape("circle");
    setShapeValue(circleValue);
  };
  const handleContourSelect = () => {
    setSelectedShape("contour");
    setShapeValue(contourValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    switch (selectedShape) {
      case "aabox":
        setAaboxValue(e.target.value);
        break;
      case "circle":
        setCircleValue(e.target.value);
        break;
      case "contour":
        setContourValue(e.target.value);
        break;
      default:
        break;
    }
    setShapeValue(e.target.value);
  };

  const handleApply = () => {
    // TODO: Validate on frontend
    // Not implementing that now, because it would be more a staff-only feature, and downside is just that you'd have to relabel or rollback to previous version
    const value = JSON.parse(shapeValue);
    const baseObject = {
      aabox: null,
      circle: null,
      contour: null,
      box: null,
    };
    switch (selectedShape) {
      case "aabox":
        baseObject.aabox = value;
        break;
      case "circle":
        baseObject.circle = value;
        break;
      case "contour":
        baseObject.contour = value;
        break;
      default:
        break;
    }
    onApply(entity.id, baseObject);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className={styles.container}>
      <div className={styles.buttonsBlock}>
        <div className={styles.buttonsBlockLeft}>
          <button
            title="Use rectangle"
            className={classNames({
              [styles.selected]: selectedShape === "aabox",
            })}
            onClick={handleAaboxSelect}
          >
            <Rectangle />
          </button>
          <button
            title="Use circle"
            className={classNames({
              [styles.selected]: selectedShape === "circle",
            })}
            onClick={handleCircleSelect}
          >
            <CircleIcon />
          </button>
          <button
            title="Use polygon"
            className={classNames({
              [styles.selected]: selectedShape === "contour",
            })}
            onClick={handleContourSelect}
          >
            <Polygon />
          </button>
        </div>

        <div className={styles.buttonsBlockRight}>
          <button title="Apply changes" onClick={handleApply}>
            <Check />
          </button>
          <button title="Discard changes" onClick={handleClose}>
            <X />
          </button>
        </div>
      </div>
      <div className={styles.shapeEditor}>
        <textarea value={shapeValue} onChange={handleChange} />
      </div>
    </div>
  );
}
